/**
 * Project Timeline of Truth
 * 
 * Commit history showing who uploaded, what changed, pass/fail, and why.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, User, FileText, CheckCircle, XCircle, AlertCircle, GitBranch } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';
import MainLayout from '@/components/layout/MainLayout';
import { EmptySnapshotState } from '@/components/project-state/EmptySnapshotState';

interface Snapshot {
  id: string;
  snapshot_name: string;
  snapshot_type: string;
  import_run_id: string;
  uploaded_at: string;
  uploaded_by: string | null;
  file_name: string | null;
  is_baseline: boolean;
  is_latest: boolean;
  compile_status: string | null;
  metadata: any;
}

export const TimelineOfTruth: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { theme } = useAppTheme();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchSnapshots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSnapshots recreated each render; projectId is key
  }, [projectId]);

  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/v1/snapshots/${projectId}`, {
        credentials: 'include',
        headers,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch snapshots');
      }
      const data = await response.json();
      setSnapshots(data.snapshots || []);
      if (data.message && data.snapshots.length === 0) {
        // This is not an error, just an informational message
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string | null) => {
    if (!status) return <AlertCircle className="h-4 w-4 text-gray-400" />;
    switch (status.toLowerCase()) {
      case 'pass':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <MainLayout title="Project Timeline of Truth">
      {loading ? (
        <div className={`flex items-center justify-center h-full ${theme === 'dark' ? 'bg-[#030712]' : 'bg-white'}`}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading timeline...</p>
          </div>
        </div>
      ) : error ? (
        <div className={`p-8 ${theme === 'dark' ? 'bg-[#030712] text-white' : 'bg-white'}`}>
          <div className="text-red-500">Error: {error}</div>
          <Button onClick={fetchSnapshots} className="mt-4">Retry</Button>
        </div>
      ) : (
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            {snapshots.length === 0 ? (
              <EmptySnapshotState
                title="No Snapshots Available"
                message="Upload a XER file to create the first snapshot and view project timeline."
                icon={<GitBranch className="h-12 w-12 text-gray-400 mx-auto mb-4 opacity-50" />}
              />
            ) : (
              <div className="space-y-4">
                {snapshots.map((snapshot, index) => (
                  <DarkPanel key={snapshot.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">{formatDate(snapshot.uploaded_at)}</span>
                          </div>
                          {snapshot.is_baseline && (
                            <Badge variant="outline" className="text-xs">Baseline</Badge>
                          )}
                          {snapshot.is_latest && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500">Latest</Badge>
                          )}
                          {getStatusIcon(snapshot.compile_status)}
                          <Badge variant={snapshot.compile_status === 'pass' ? 'success' : snapshot.compile_status === 'warning' ? 'warning' : 'destructive'}>
                            {snapshot.compile_status ? snapshot.compile_status.charAt(0).toUpperCase() + snapshot.compile_status.slice(1) : 'Pending'}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold mb-1">{snapshot.snapshot_name}</h3>
                        
                        {snapshot.file_name && (
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                            <FileText className="h-3 w-3" />
                            <span>{snapshot.file_name}</span>
                          </div>
                        )}
                        
                        {snapshot.metadata && (
                          <div className="text-xs text-gray-500 mt-2">
                            {snapshot.metadata?.total_activities || 0} activities, {snapshot.metadata?.total_relationships || 0} relationships
                          </div>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/project-state/${projectId}/change-intelligence?from=${snapshot.id}`)}
                      >
                        View Diff
                      </Button>
                    </div>
                  </DarkPanel>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default TimelineOfTruth;

